// A passphrase prompt — `todo-takeout.md` entry 6.25.
//
// Key export: PassphrasePrompt.
// Neighbours: shell/WatchLayout.tsx (unlocking), shell/Sidebar.tsx (locking),
// @synvya/portal-ui/ui `SystemDialog` (the shape this follows).
//
// **Not `SystemDialog`, because that one carries no input.** Its props are a
// title, a body and actions; a passphrase needs a field, a confirmation field
// when setting one, and an error line that survives a wrong answer. This keeps
// its shape — centred, portalled, escape closes — so the two read as the same
// family.
//
// **The value never leaves this component.** It is handed to `onSubmit` and
// dropped when the dialog closes; nothing here logs it, stores it, or puts it
// in a URL.

import { useEffect, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/Button'

export interface PassphrasePromptProps {
  open: boolean
  title: string
  body: string
  confirmLabel: string
  /** Ask twice and require a match. For setting a passphrase, not entering one. */
  requireConfirmation?: boolean
  onCancel: () => void
  /** Returns an error to show, or null when the passphrase was accepted. */
  onSubmit: (passphrase: string) => Promise<string | null>
}

export function PassphrasePrompt({
  open,
  title,
  body,
  confirmLabel,
  requireConfirmation = false,
  onCancel,
  onSubmit,
}: PassphrasePromptProps) {
  const [value, setValue] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const fieldRef = useRef<HTMLInputElement>(null)

  // Cleared on every open, so a passphrase typed and abandoned is not sitting
  // in a field the next person to open this dialog can read.
  useEffect(() => {
    if (!open) return
    setValue('')
    setAgain('')
    setError(null)
    setBusy(false)
    fieldRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    if (value.trim() === '') {
      setError('Enter a passphrase.')
      return
    }
    if (requireConfirmation && value !== again) {
      // Caught here rather than after locking: a mistyped passphrase on a
      // screen nobody can unlock is the failure this dialog exists to avoid.
      setError('The two passphrases do not match.')
      return
    }
    setBusy(true)
    const failure = await onSubmit(value)
    setBusy(false)
    if (failure) {
      setError(failure)
      setValue('')
      setAgain('')
      fieldRef.current?.focus()
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} aria-hidden />
      <form
        onSubmit={handleSubmit}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-sm space-y-3 rounded-xl border border-border bg-card p-5 shadow-xl"
      >
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{body}</p>

        <label className="block">
          <span className="sr-only">{title}</span>
          <input
            ref={fieldRef}
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>

        {requireConfirmation && (
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Enter it again</span>
            <input
              type="password"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
              autoComplete="off"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </label>
        )}

        {error && (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {confirmLabel}
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  )
}
